import { VARIABLE_DEFINITIONS } from './proposalVariables'

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'H3', 'H4', 'A', 'DEL', 'S', 'STRIKE', 'SPAN'])
const TAG_NORMALIZATIONS = {
  B: 'strong',
  I: 'em',
  S: 'del',
  STRIKE: 'del'
}
// Block tags allowed to carry a text-align style (emitted by the WYSIWYG).
const ALIGNABLE_TAGS = new Set(['p', 'h3', 'h4', 'li'])
const ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify'])
const FONT_SIZE_VALUES = new Set(['11px', '12px', '13px', '14px', '16px', '18px', '20px', '24px'])
export const RICH_TEXT_SPACING_OPTIONS = [
  { value: 'normal', label: 'Normal', style: {} },
  { value: 'tight', label: 'Tight', style: { 'line-height': '1.35', 'margin-bottom': '6px' } },
  { value: 'roomy', label: 'Roomy', style: { 'line-height': '1.7', 'margin-bottom': '18px' } },
  { value: 'section', label: 'Section Gap', style: { 'margin-bottom': '28px' } }
]
const LINE_HEIGHT_VALUES = new Set(RICH_TEXT_SPACING_OPTIONS.map(option => option.style['line-height']).filter(Boolean))
const MARGIN_BOTTOM_VALUES = new Set(RICH_TEXT_SPACING_OPTIONS.map(option => option.style['margin-bottom']).filter(Boolean))

const CHARS_PER_LINE = 86
const LINE_HEIGHT = 24
const BLOCK_GAP = 12
const INTRO_PAGE_HEIGHT = 870
const GREETING_HEIGHT = 46
const CLOSING_HEIGHT = 148
const NOTES_TITLE_HEIGHT = 78
const OVERSIZED_BLOCK_CHARS = 900

export function escapeHtml (value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function plainTextToHtml (text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .split(/\n{2,}/)
    .map(section => section.trim())
    .filter(Boolean)
    .map(section => `<p>${section.split('\n').map(line => escapeHtml(line)).join('<br />')}</p>`)
    .join('')
}

function canParseHtml () {
  return typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined'
}

function safeHref (node) {
  const href = String(node.getAttribute('href') || '').trim()
  if (/^(https?:|mailto:)/i.test(href)) return href
  return ''
}

function serializeNode (node) {
  if (node.nodeType === window.Node.TEXT_NODE) return escapeHtml(node.textContent || '')
  if (node.nodeType !== window.Node.ELEMENT_NODE) return ''

  const tagName = node.tagName
  const children = Array.from(node.childNodes).map(serializeNode).join('')
  if (!ALLOWED_TAGS.has(tagName)) return children

  const normalized = TAG_NORMALIZATIONS[tagName] || tagName.toLowerCase()
  if (normalized === 'br') return '<br />'

  let attrs = ''
  if (normalized === 'a') {
    const href = safeHref(node)
    if (!href) return children
    attrs = ` href="${escapeHtml(href)}"`
  } else if (normalized === 'span') {
    const fontSize = String(node.style?.fontSize || '').toLowerCase().replace(/\s+/g, '')
    if (FONT_SIZE_VALUES.has(fontSize)) attrs = ` style="font-size:${fontSize};"`
    if (!attrs) return children
  } else if (ALIGNABLE_TAGS.has(normalized)) {
    const styles = []
    const align = String(node.style?.textAlign || '').toLowerCase()
    if (ALIGN_VALUES.has(align) && align !== 'left') styles.push(`text-align:${align};`)
    const lineHeight = String(node.style?.lineHeight || '').toLowerCase().replace(/\s+/g, '')
    if (LINE_HEIGHT_VALUES.has(lineHeight)) styles.push(`line-height:${lineHeight};`)
    const marginBottom = String(node.style?.marginBottom || '').toLowerCase().replace(/\s+/g, '')
    if (MARGIN_BOTTOM_VALUES.has(marginBottom)) styles.push(`margin-bottom:${marginBottom};`)
    if (styles.length) attrs = ` style="${styles.join('')}"`
  }
  return `<${normalized}${attrs}>${children}</${normalized}>`
}

export function sanitizeRichHtml (html) {
  if (!html || typeof html !== 'string') return ''
  if (!canParseHtml()) return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')

  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstChild
  return Array.from(root.childNodes).map(serializeNode).join('')
}

function formatVariableValue (key, variables) {
  if (!variables || variables[key] === undefined) return `{{${key}}}`

  const def = VARIABLE_DEFINITIONS.find(d => d.key === key)
  const val = variables[key]
  if (def && def.type === 'currency') return '$' + Number(val).toFixed(2)
  if (def && def.type === 'select' && def.options) {
    const opt = def.options.find(o => o.value === val)
    if (opt) return opt.label
  }
  return String(val)
}

export function interpolateRichHtml (html, variables) {
  return sanitizeRichHtml(html).replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (!variables || variables[key] === undefined) return match
    return escapeHtml(formatVariableValue(key, variables))
  })
}

export function getIntroBodyHtml (data) {
  if (data?.bodyHtml) return data.bodyHtml
  return plainTextToHtml(data?.body || '')
}

export function getRichBodyHtml (data) {
  if (data?.bodyHtml) return data.bodyHtml
  return plainTextToHtml(data?.body || '')
}

function textFromHtml (html) {
  if (!html) return ''
  if (!canParseHtml()) return html.replace(/<[^>]+>/g, ' ')
  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  return doc.body.textContent || ''
}

// Empty paragraphs (<p></p>, <p><br /></p>, whitespace-only) come from stray
// Enter presses in the WYSIWYG. They render as invisible blank lines AND count
// against the page-height budget, causing pages to break early with big gaps.
function isEmptyBlock (html) {
  if (/<(li|img)/i.test(html)) return false
  return !textFromHtml(html).replace(/\u00a0/g, " ").trim()
}

function splitRichHtmlBlocks (html) {
  const sanitized = sanitizeRichHtml(html)
  if (!sanitized) return []
  if (!canParseHtml()) return [sanitized]

  const doc = new window.DOMParser().parseFromString(`<div>${sanitized}</div>`, 'text/html')
  const root = doc.body.firstChild
  const blocks = Array.from(root.childNodes)
    .map((node) => serializeNode(node))
    .filter(Boolean)
    .filter((block) => !isEmptyBlock(block))
  return blocks.length ? blocks : []
}

function styleValueFromHtml (html, property) {
  const match = String(html || '').match(/\sstyle="([^"]*)"/i)
  if (!match) return ''
  const styles = match[1].split(';').map(part => part.trim()).filter(Boolean)
  const entry = styles.find(part => part.toLowerCase().startsWith(`${property}:`))
  return entry ? entry.split(':').slice(1).join(':').trim().toLowerCase() : ''
}

function estimateBlockHeight (html) {
  const text = textFromHtml(html).trim()
  const brCount = (html.match(/<br\s*\/?>/gi) || []).length
  const listItemCount = (html.match(/<li[\s>]/gi) || []).length
  const headingBonus = /<h[34](\s|>)/.test(html) ? 10 : 0
  const lines = Math.max(1, Math.ceil(text.length / CHARS_PER_LINE) + brCount)
  const lineHeightStyle = styleValueFromHtml(html, 'line-height')
  const marginBottomStyle = styleValueFromHtml(html, 'margin-bottom')
  const lineHeight = LINE_HEIGHT_VALUES.has(lineHeightStyle)
    ? Math.round(14 * Number(lineHeightStyle))
    : LINE_HEIGHT
  const blockGap = MARGIN_BOTTOM_VALUES.has(marginBottomStyle)
    ? Number(marginBottomStyle.replace('px', ''))
    : BLOCK_GAP
  const listBonus = listItemCount ? listItemCount * 6 : 0
  return (lines * lineHeight) + blockGap + listBonus + headingBonus
}

function isHeadingBlock (html) {
  return /^<h[34][\s>]/i.test(String(html || '').trim())
}

function groupHeadingBlocks (blocks) {
  const groups = []
  for (let i = 0; i < blocks.length; i += 1) {
    if (isHeadingBlock(blocks[i]) && blocks[i + 1]) {
      groups.push([blocks[i], blocks[i + 1]])
      i += 1
    } else {
      groups.push([blocks[i]])
    }
  }
  return groups
}

function splitOversizedBlock (html) {
  if (estimateBlockHeight(html) <= INTRO_PAGE_HEIGHT - CLOSING_HEIGHT) return [html]
  const text = textFromHtml(html).trim()
  if (!text || text.length <= OVERSIZED_BLOCK_CHARS) return [html]

  const chunks = []
  let current = ''
  text.split(/\s+/).forEach((word) => {
    if (current && `${current} ${word}`.length > OVERSIZED_BLOCK_CHARS) {
      chunks.push(`<p>${escapeHtml(current)}</p>`)
      current = word
    } else {
      current = current ? `${current} ${word}` : word
    }
  })
  if (current) chunks.push(`<p>${escapeHtml(current)}</p>`)
  return chunks
}

export function paginateIntroLetter (data) {
  const blocks = splitRichHtmlBlocks(getIntroBodyHtml(data)).flatMap(splitOversizedBlock)
  if (!blocks.length) return [{ ...data, bodyHtml: '', showGreeting: true, showClosing: true }]

  const pages = []
  let currentBlocks = []
  let currentHeight = GREETING_HEIGHT
  const maxBodyHeight = INTRO_PAGE_HEIGHT - CLOSING_HEIGHT

  blocks.forEach((block) => {
    const blockHeight = estimateBlockHeight(block)
    if (currentBlocks.length && currentHeight + blockHeight > maxBodyHeight) {
      pages.push(currentBlocks)
      currentBlocks = [block]
      currentHeight = blockHeight
    } else {
      currentBlocks.push(block)
      currentHeight += blockHeight
    }
  })

  if (currentBlocks.length) pages.push(currentBlocks)

  return pages.map((pageBlocks, index) => ({
    ...data,
    bodyHtml: pageBlocks.join(''),
    body: '',
    showGreeting: index === 0,
    showClosing: index === pages.length - 1
  }))
}

export function paginateNotesPage (data) {
  const blocks = splitRichHtmlBlocks(getRichBodyHtml(data)).flatMap(splitOversizedBlock)
  if (!blocks.length) return [{ ...data, bodyHtml: '', body: '', showTitle: true }]

  const pages = []
  let currentBlocks = []
  let currentHeight = NOTES_TITLE_HEIGHT
  const maxBodyHeight = INTRO_PAGE_HEIGHT

  groupHeadingBlocks(blocks).forEach((group) => {
    const groupHeight = group.reduce((sum, block) => sum + estimateBlockHeight(block), 0)
    if (currentBlocks.length && currentHeight + groupHeight > maxBodyHeight) {
      pages.push(currentBlocks)
      currentBlocks = [...group]
      currentHeight = groupHeight
    } else {
      currentBlocks.push(...group)
      currentHeight += groupHeight
    }
  })

  if (currentBlocks.length) pages.push(currentBlocks)

  return pages.map((pageBlocks, index) => ({
    ...data,
    bodyHtml: pageBlocks.join(''),
    body: '',
    showTitle: index === 0
  }))
}
